import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { track, itemFromProduct } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WANT = 8; // how many cards we aim to show
const FETCH_LIMIT = 12; // over-fetch so we can drop the current product + dedupe

// Item-list identity for GA4, so recommendation clicks are separable from feed clicks.
const REC_LIST = "recommendations:more_like_this";

// "More like this" strip shown on the product detail page.
//
// Recommendations always match the size(s) of the product being viewed — a
// UK 12 shoe surfaces other UK 12 shoes — so the strip never contradicts the
// item on screen. For clothing, a thin size-matched set is topped up with other
// pieces from the same category so the strip is never empty; shoes are matched
// strictly instead (see below), because a mismatched shoe size is simply wrong.
const HeightRecommendations = ({ product }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const cat = product?.category_info;
  const isShoe = cat?.kind === "shoes";
  const sizeLabel = (cat?.size_label || "ft").trim() || "ft";
  const categoryId = product?.category_id || null;

  useEffect(() => {
    if (!product?.id) return;
    let cancelled = false;
    setLoading(true);

    // Match on the size(s) of THIS product (a product can carry a few).
    const targetHeights = (product?.heights || []).filter(Boolean);

    const fetchList = async (params) => {
      try {
        const res = await axios.get(`${API}/products`, { params });
        return res.data?.items || [];
      } catch (_) {
        return [];
      }
    };

    (async () => {
      const base = { limit: FETCH_LIMIT };
      if (categoryId) base.category_id = categoryId;

      const seen = new Set([product.id]);
      const picked = [];
      const addFrom = (list) => {
        for (const it of list) {
          if (picked.length >= WANT) break;
          if (seen.has(it.id)) continue;
          seen.add(it.id);
          picked.push(it);
        }
      };

      // Size-matched primary pull: one query per target size, merged in order.
      // Capped so a many-size product doesn't fan out into too many requests —
      // the top-up below fills any remainder.
      if (targetHeights.length) {
        const lists = await Promise.all(
          targetHeights.slice(0, 3).map((h) => fetchList({ ...base, height: h }))
        );
        lists.forEach(addFrom);
      }

      // Top up from the same category (any size) if the matched set is thin,
      // so the strip is never empty.
      //
      // Shoes opt out: footwear sizing has no tolerance the way a height range
      // does, so a UK 12 shoe sitting beside a UK 8 is a wrong answer rather
      // than a thin one — and the card's own size badge makes the mismatch
      // plain to see. Better to show fewer cards, or none.
      // The exception is a shoe with no recorded size: there's no promise to
      // break, so the top-up still runs rather than leaving an empty strip.
      const strictSize = isShoe && targetHeights.length > 0;
      if (picked.length < WANT && !strictSize) addFrom(await fetchList(base));

      if (cancelled) return;
      const shown = picked.slice(0, WANT);
      setItems(shown);
      setLoading(false);
      if (shown.length > 0) {
        track("view_item_list", {
          item_list_id: REC_LIST,
          item_list_name: REC_LIST,
          source_item_id: product.id,
          items: shown.map((it, i) =>
            itemFromProduct(it, { index: i, item_list_name: REC_LIST })
          ),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, categoryId]);

  if (loading) {
    return (
      <div className="mt-14 md:mt-20 border-t border-[#E4E1D9] pt-10 md:pt-14">
        <div className="h-4 w-56 bg-[#E4E1D9] rounded animate-pulse mb-5" />
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="shrink-0 w-40 md:w-48">
              <div className="w-full aspect-[1/2] bg-[#E4E1D9] rounded-md animate-pulse" />
              <div className="h-3 w-3/4 bg-[#E4E1D9] rounded mt-3 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <section
      className="mt-14 md:mt-20 border-t border-[#E4E1D9] pt-10 md:pt-14"
      data-testid="height-recommendations"
    >
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <h2
          className="text-2xl md:text-3xl tracking-tight"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
          data-testid="recommendations-heading"
        >
          More like this
        </h2>
      </div>

      {/* The negative margin + padding lets cards scroll to the screen edge
          while still starting at the page's content inset. scroll-pl-6 is what
          makes that inset survive: snap-x aligns the first card to the
          scrollport, which ignores padding unless scroll-padding matches it —
          without it the browser scrolls 24px on load and the first card sits
          flush against the screen edge, out of line with everything else. */}
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-6 px-6 scroll-pl-6 md:mx-0 md:px-0 md:scroll-pl-0 snap-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {items.map((it) => {
          // Recommendations are scoped to the current product's category, so its
          // size label/kind applies to them too (list items carry no category_info).
          const sizes = (it.heights || []).join(", ");
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => {
                // The strip is a recommendation surface, so it gets the same
                // list treatment as the feed — select_item with its own list
                // name, which is the only way to tell whether it earns its space.
                track("select_item", {
                  item_list_id: REC_LIST,
                  item_list_name: REC_LIST,
                  items: [
                    itemFromProduct(it, {
                      index: items.indexOf(it),
                      item_list_name: REC_LIST,
                    }),
                  ],
                  source_item_id: product?.id,
                });
                navigate(`/product/${it.id}`);
              }}
              className="group shrink-0 w-40 md:w-48 snap-start text-left"
              data-testid={`recommendation-${it.id}`}
            >
              <div className="w-full overflow-hidden bg-white border border-[#E4E1D9]">
                {it.image_url && (
                  <img
                    src={`${BACKEND_URL}${it.image_url}`}
                    alt={it.product_name}
                    className="w-full aspect-[1/2] object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                    loading="lazy"
                  />
                )}
              </div>
              <h3
                className="text-sm md:text-base tracking-tight line-clamp-1 mt-2 group-hover:opacity-70 transition-opacity"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {it.product_name}
              </h3>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {it.price && (
                  <span
                    className="text-[10px] uppercase tracking-[0.1em] text-white bg-[#2D302A] px-2 py-0.5 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {it.price}
                  </span>
                )}
                {sizes && (
                  <span
                    className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F0EFEB] px-2 py-0.5 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {isShoe ? `${sizeLabel} ${sizes}` : `${sizes} ${sizeLabel}`}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default HeightRecommendations;
